'use client'

// Sacrifices renderer — draws the SACRIFICE entities the sim publishes as
// crystal SHARDS. The sim owns everything spatial: PENDING offerings are
// scattered across the map (obstacle-free) for Victor to find; ACCEPTED ones
// have been migrated to a fixed ruins display spot; REJECTED are removed
// server-side and never arrive here. We just render a shard at entity.pos,
// tinted + sized by the submitter's TIER (shared map in lib/world/
// sacrificeTiers.js). The shard's own material carries the edge-damage +
// per-shard tilt/size jitter, and RendererDither stipples it with the world.

import { useMemo } from 'react'
import { useTerrainHeight } from '../terrain/TerrainHeightProvider'
import useGameStore from '../../../stores/gameStore'
import { useStoreWithEqualityFn } from 'zustand/traditional'
import { FEATURES } from '../../../lib/features'
import { Shard } from './Shard'
import { tierLook } from '../../../lib/world/sacrificeTiers'

// Per-type accent: tributes read emerald, images sky-blue; word offerings keep
// their tier colour. (The dock/shrine feeds carry the full per-type breakdown.)
const TYPE_TINT = { financial: '#34d399', image: '#38bdf8' }

export function Sacrifices() {
  if (!FEATURES.shrine) return null
  return <SacrificesActive />
}

function SacrificesActive() {
  // Custom equality: only re-render when SACRIFICE entities meaningfully
  // change (count, id, phase, tier, or position). Skips re-renders from the
  // far more frequent food/nest deltas.
  const entityStates = useStoreWithEqualityFn(
    useGameStore,
    (s) => s.entityStates,
    (prev, next) => {
      const a = prev.filter((e) => e.type === 'SACRIFICE')
      const b = next.filter((e) => e.type === 'SACRIFICE')
      if (a.length !== b.length) return false
      for (let i = 0; i < a.length; i++) {
        const x = a[i], y = b[i]
        if (x.id !== y.id) return false
        if (x.state?.phase !== y.state?.phase) return false
        if (x.state?.tier !== y.state?.tier) return false
        if (x.pos.x !== y.pos.x || x.pos.y !== y.pos.y || x.pos.z !== y.pos.z) return false
      }
      return true
    },
  )

  const sacrifices = useMemo(
    () => entityStates.filter((e) => e.type === 'SACRIFICE'),
    [entityStates],
  )

  const terrainField = useTerrainHeight()

  if (sacrifices.length === 0) return null

  return (
    <group>
      {sacrifices.map((entity) => {
        const { color, scale } = tierLook(entity.state?.tier)
        const shardColor = TYPE_TINT[entity.state?.kind] || color
        const pending = entity.state?.phase === 'pending'
        // PENDING offerings sit on the ground they landed on: the sim
        // scatters them XZ-only at y=0 and the town has hills, so without
        // the snap an offering on high ground renders inside the hillside.
        // ACCEPTED crystals are the opposite case and the snap was the bug:
        // their display spots are AUTHORED with real heights, up on the
        // ruins' arches and ledges, and flattening them to the floor threw
        // all 27 of those y values away. Seated crystals keep their y.
        const groundY = pending && terrainField ? terrainField.sample(entity.pos.x, entity.pos.z) : null
        const y = Number.isFinite(groundY) ? groundY : (entity.pos.y ?? 0)
        return (
          <group key={entity.id}>
            <Shard
              seed={entity.id}
              color={shardColor}
              scale={scale}
              position={[entity.pos.x, y, entity.pos.z]}
            />
            {/* Floor ring under an unjudged offering so it's easy to find in the
                world; it clears once Victor has judged it (phase leaves pending). */}
            {pending && (
              <mesh position={[entity.pos.x, y + 0.06, entity.pos.z]} rotation={[-Math.PI / 2, 0, 0]}>
                <ringGeometry args={[1.5, 2.05, 48]} />
                <meshBasicNodeMaterial color={shardColor} transparent opacity={0.4} depthWrite={false} />
              </mesh>
            )}
          </group>
        )
      })}
    </group>
  )
}

export default Sacrifices
